import React, { useState, useEffect } from 'react'
import {
    Dialog,
    DialogContent,
    DialogTitle,
    useMediaQuery,
    Tab,
    Tabs,
    Box,
    MenuItem,
    FormControlLabel,
    Checkbox,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Content, Campo } from './styles'
import axios from '../../../services/withAxios';
import Cliente from '../../../interfaces/cliente'
import MaskCpf from '../../../utils/MaskCpf/index'
import MaskCnpj from '../../../utils/MaskCnpj'
import MaskCep from '../../../utils/MaskCep'
import MaskTelefone from '../../../utils/MaskTelefone'
import PopupProps from '../../../interfaces/popupProps'
import ButtonsCrud from '../ButtonsCrud'
import { DesabilitarCampoCrud } from '../../../services/auth';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    abaValue: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, abaValue, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={abaValue !== index}
            id={`cliente-tabpanel-${index}`}
            aria-labelledby={`cliente-tab-${index}`}
            {...other}
        >
            {abaValue === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const ClienteDialog: React.FC<PopupProps<Cliente>> = ({ aberto, setAberto, entidade }) => {
        const [aba, setAba] = useState<number>(0)

        const [nome, setNome] = useState<string>('')
        const [email, setEmail] = useState<string>('')
        const [emailError, setEmailError] = useState<boolean>(false);
        const [telefone, setTelefone] = useState<string>('')
        const [cpf, setCpf] = useState<string>('')
        const [cnpj, setCnpj] = useState<string>('')
        const [observacao, setObservacao] = useState<string>('')

        const [logradouro, setLogradouro] = useState<string>('')
        const [numero, setNumero] = useState<string>('')
        const [complemento, setComplemento] = useState<string>('')
        const [bairro, setBairro] = useState<string>('')
        const [municipio, setMunicipio] = useState<string>('')
        const [codigoMunicipio, setCodigoMunicipio] = useState<number | undefined>()
        const [estado, setEstado] = useState<string>('')
        const [codigoEstado, setCodigoEstado] = useState<number | undefined>()
        const [cep, setCep] = useState<string>('')
        const [inscricaoEstadual, setInscricaoEstadual] = useState<string>('')
        const [indicadorIE, setIndicadorIE] = useState<number | ''>('')
        const [revenda, setRevenda] = useState<boolean>(false)
        const [mostrarErrosEndereco, setMostrarErrosEndereco] = useState<boolean>(false)

        const enderecoObrigatorio = cnpj.replace(/\D/g, '').length > 0

        const theme = useTheme()
        const fullScreen = useMediaQuery(theme.breakpoints.down('md'))

        const handleFechar = () => {
            setAberto(false)
        }

        // A Inscricao Estadual so e exigida de CONTRIBUINTE de ICMS (indIEDest 1). Isento (2) e nao
        // contribuinte (9) emitem NF-e sem IE - e a mesma regra aplicada no backend.
        const ehContribuinteIcms = indicadorIE === 1

        // O codigo IBGE do municipio e obrigatorio no endereco do destinatario da NF-e, mas NAO
        // entra no enderecoValido(): o campo e somente leitura e nada o preenche ainda (ver
        // TECH_DEBT TD-010), entao exigi-lo aqui impediria o cadastro de QUALQUER cliente com CNPJ.
        // A cobranca fica no backend, na emissao, que ja diz em portugues qual campo falta.
        const codigoMunicipioPreenchido = (codigoMunicipio ?? 0) > 0

        const enderecoValido = () =>
            !!(logradouro.trim()
                && numero.trim()
                && bairro.trim()
                && municipio.trim()
                && estado.trim()
                && cep.replace(/\D/g, '').length > 0
                && (!ehContribuinteIcms || inscricaoEstadual.trim()))

        const montarCliente = (id?: string): Cliente => ({
            ...(id ? { id } : {}),
            nome: nome.toUpperCase(),
            email,
            telefone,
            cpf,
            cnpj: cnpj || null,
            observacao: observacao.toUpperCase(),
            logradouro: logradouro ? logradouro.toUpperCase() : null,
            numero: numero || null,
            complemento: complemento ? complemento.toUpperCase() : null,
            bairro: bairro ? bairro.toUpperCase() : null,
            municipio: municipio ? municipio.toUpperCase() : null,
            codigoMunicipio: codigoMunicipio ?? null,
            estado: estado ? estado.toUpperCase() : null,
            codigoEstado: codigoEstado ?? null,
            cep: cep || null,
            inscricaoEstadual: inscricaoEstadual ? inscricaoEstadual.toUpperCase() : null,
            indicadorIE: indicadorIE === '' ? null : indicadorIE,
            revenda,
        })

        const handleSalvar = (e: React.SyntheticEvent) => {
            e.preventDefault()

            if (enderecoObrigatorio && !enderecoValido()) {
                setMostrarErrosEndereco(true)
                setAba(1)
                return
            }

            setMostrarErrosEndereco(false)

            if (entidade) {
                axios.put('/cliente', montarCliente(entidade.id))
                    .then(resp => {
                        if (resp.status === 200)
                            setAberto(false)
                    })
            } else {
                axios.post('/cliente', montarCliente())
                    .then(resp => {
                        if (resp.status === 200)
                            setAberto(false)
                    })
            }
        }

        const handleDeletar = () => {
            if (entidade?.id) {
                axios.delete('/cliente/' + entidade.id).then(resp => {
                    if (resp.status === 200)
                        setAberto(false)
                })
            }
        }

        const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            setEmail(value);

            // Validação simples de e-mail usando regex
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            setEmailError(!emailRegex.test(value) && value.length > 0);
        }

        const handleAbaChange = (_event: React.SyntheticEvent, valorAba: number) => {
            setAba(valorAba);
        };

        useEffect(() => {
            if (entidade) {
                setNome(entidade.nome)
                setEmail(entidade.email)
                setTelefone(entidade.telefone)
                setCpf(entidade.cpf)
                setCnpj(entidade.cnpj ?? '')
                setObservacao(entidade.observacao)
                setLogradouro(entidade.logradouro ?? '')
                setNumero(entidade.numero ?? '')
                setComplemento(entidade.complemento ?? '')
                setBairro(entidade.bairro ?? '')
                setMunicipio(entidade.municipio ?? '')
                setCodigoMunicipio(entidade.codigoMunicipio ?? undefined)
                setEstado(entidade.estado ?? '')
                setCodigoEstado(entidade.codigoEstado ?? undefined)
                setCep(entidade.cep ?? '')
                setInscricaoEstadual(entidade.inscricaoEstadual ?? '')
                setIndicadorIE(entidade.indicadorIE ?? '')
                setRevenda(entidade.revenda ?? false)
            }
        }, [aberto, entidade])

        return (
            <Dialog
                fullScreen={fullScreen}
                open={aberto}
                onClose={handleFechar}
            >
                <DialogTitle id="cliente-dialog">Cliente</DialogTitle>
                <DialogContent>
                    <Content id="formCliente" component="form" onSubmit={handleSalvar}>
                        <TabPanel abaValue={aba} index={0}>
                            <Campo
                                id="nome"
                                label="Nome"
                                value={nome}
                                onChange={(e) => setNome(e.target.value)}
                                required
                                fullWidth
                                autoComplete='off'
                                autoFocus
                                inputProps={{ style: { textTransform: "uppercase" } }}
                                disabled={DesabilitarCampoCrud()}
                            />
                            <Campo
                                id="email"
                                label="Email"
                                type="email"
                                error={emailError}
                                value={email}
                                onChange={handleEmailChange}
                                fullWidth
                                autoComplete='off'
                                disabled={DesabilitarCampoCrud()}
                            />
                            <Campo
                                id="telefone"
                                label="Telefone"
                                value={telefone}
                                placeholder="(00) 00000-0000"
                                onChange={(e) => setTelefone(e.target.value)}
                                fullWidth
                                type="tel"
                                InputProps={{
                                    inputComponent: MaskTelefone as any,
                                }}
                                autoComplete='off'
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ maxLength: 25 }}
                                disabled={DesabilitarCampoCrud()}
                            />
                            <Campo
                                id="cpf"
                                label="CPF"
                                value={cpf}
                                placeholder="000.000.000-00"
                                onChange={(e) => setCpf(e.target.value)}
                                required={!enderecoObrigatorio}
                                fullWidth
                                InputProps={{
                                    inputComponent: MaskCpf as any
                                }}
                                autoComplete='off'
                                InputLabelProps={{ shrink: true }}
                                disabled={DesabilitarCampoCrud()}
                            />
                            <Campo
                                id="cnpj"
                                label="CNPJ"
                                value={cnpj}
                                placeholder="00.000.000/0000-00"
                                onChange={(e) => setCnpj(e.target.value)}
                                fullWidth
                                InputProps={{
                                    inputComponent: MaskCnpj as any
                                }}
                                autoComplete='off'
                                InputLabelProps={{ shrink: true }}
                                disabled={DesabilitarCampoCrud()}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        id="revenda"
                                        checked={revenda}
                                        onChange={(e) => setRevenda(e.target.checked)}
                                        disabled={DesabilitarCampoCrud()}
                                    />
                                }
                                label="Compra para revenda (não é consumidor final)"
                                sx={{ width: '100%' }}
                            />
                            <Campo
                                id="observacao"
                                label="Observação"
                                value={observacao}
                                onChange={(e) => setObservacao(e.target.value)}
                                fullWidth
                                autoComplete='off'
                                inputProps={{ style: { textTransform: "uppercase" } }}
                                disabled={DesabilitarCampoCrud()}
                            />
                        </TabPanel>
                        <TabPanel abaValue={aba} index={1}>
                            <Campo
                                id="logradouro"
                                label="Logradouro"
                                value={logradouro}
                                onChange={(e) => setLogradouro(e.target.value)}
                                required={enderecoObrigatorio}
                                error={mostrarErrosEndereco && !logradouro.trim()}
                                fullWidth
                                autoComplete='off'
                                inputProps={{ maxLength: 50, style: { textTransform: "uppercase" } }}
                                disabled={DesabilitarCampoCrud()}
                            />
                            <Campo
                                id="numero"
                                label="Número"
                                value={numero}
                                onChange={(e) => setNumero(e.target.value)}
                                required={enderecoObrigatorio}
                                error={mostrarErrosEndereco && !numero.trim()}
                                fullWidth
                                autoComplete='off'
                                inputProps={{ maxLength: 10 }}
                                disabled={DesabilitarCampoCrud()}
                            />
                            <Campo
                                id="complemento"
                                label="Complemento"
                                value={complemento}
                                onChange={(e) => setComplemento(e.target.value)}
                                fullWidth
                                autoComplete='off'
                                inputProps={{ maxLength: 60, style: { textTransform: "uppercase" } }}
                                disabled={DesabilitarCampoCrud()}
                            />
                            <Campo
                                id="bairro"
                                label="Bairro"
                                value={bairro}
                                onChange={(e) => setBairro(e.target.value)}
                                required={enderecoObrigatorio}
                                error={mostrarErrosEndereco && !bairro.trim()}
                                fullWidth
                                autoComplete='off'
                                inputProps={{ maxLength: 30, style: { textTransform: "uppercase" } }}
                                disabled={DesabilitarCampoCrud()}
                            />
                            <Campo
                                id="municipio"
                                label="Município"
                                value={municipio}
                                onChange={(e) => setMunicipio(e.target.value)}
                                required={enderecoObrigatorio}
                                error={mostrarErrosEndereco && !municipio.trim()}
                                fullWidth
                                autoComplete='off'
                                inputProps={{ maxLength: 30, style: { textTransform: "uppercase" } }}
                                disabled={DesabilitarCampoCrud()}
                            />
                            <Campo
                                id="codigo-municipio"
                                label="Código Município (IBGE)"
                                value={codigoMunicipio ?? ''}
                                // Sem 'error': o campo nao e editavel, entao pintar de vermelho so
                                // acusaria o usuario de algo que ele nao pode resolver. O aviso abaixo
                                // explica de quem e a responsabilidade.
                                helperText={
                                    enderecoObrigatorio && !codigoMunicipioPreenchido
                                        ? 'Necessário para emitir NF-e. Será preenchido pela integração com o IBGE (pendente).'
                                        : ''
                                }
                                fullWidth
                                type="number"
                                autoComplete='off'
                                InputLabelProps={{ shrink: true }}
                                // Somente leitura de proposito: digitar codigo IBGE a mao e arriscado
                                // (um codigo errado gera nota com municipio incorreto, sem aviso).
                                // Sera preenchido pela integracao com o IBGE/CEP. Ver TECH_DEBT.
                                disabled
                            />
                            <Campo
                                id="estado"
                                label="Estado (UF)"
                                value={estado}
                                onChange={(e) => setEstado(e.target.value)}
                                required={enderecoObrigatorio}
                                error={mostrarErrosEndereco && !estado.trim()}
                                fullWidth
                                autoComplete='off'
                                placeholder="SC"
                                inputProps={{ maxLength: 2, style: { textTransform: "uppercase" } }}
                                disabled={DesabilitarCampoCrud()}
                            />
                            <Campo
                                id="codigo-estado"
                                label="Código Estado"
                                value={codigoEstado ?? ''}
                                fullWidth
                                type="number"
                                autoComplete='off'
                                InputLabelProps={{ shrink: true }}
                                disabled
                            />
                            <Campo
                                id="cep"
                                label="CEP"
                                value={cep}
                                placeholder="00000-000"
                                onChange={(e) => setCep(e.target.value)}
                                required={enderecoObrigatorio}
                                error={mostrarErrosEndereco && cep.replace(/\D/g, '').length === 0}
                                fullWidth
                                InputProps={{
                                    inputComponent: MaskCep as any,
                                }}
                                autoComplete='off'
                                InputLabelProps={{ shrink: true }}
                                disabled={DesabilitarCampoCrud()}
                            />
                            <Campo
                                id="inscricao-estadual"
                                label={ehContribuinteIcms ? 'Inscrição Estadual' : 'Inscrição Estadual (opcional)'}
                                value={inscricaoEstadual}
                                onChange={(e) => setInscricaoEstadual(e.target.value)}
                                required={enderecoObrigatorio && ehContribuinteIcms}
                                error={mostrarErrosEndereco && ehContribuinteIcms && !inscricaoEstadual.trim()}
                                fullWidth
                                autoComplete='off'
                                inputProps={{ maxLength: 20, style: { textTransform: "uppercase" } }}
                                disabled={DesabilitarCampoCrud()}
                            />
                            <Campo
                                id="indicador-ie"
                                select
                                label="Indicador de IE (NF-e)"
                                value={indicadorIE}
                                onChange={(e) => setIndicadorIE(e.target.value === '' ? '' : Number(e.target.value))}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                disabled={DesabilitarCampoCrud()}
                            >
                                <MenuItem value="">Não informado (derivar pela IE)</MenuItem>
                                <MenuItem value={1}>1 - Contribuinte de ICMS</MenuItem>
                                <MenuItem value={2}>2 - Contribuinte isento de inscrição</MenuItem>
                                <MenuItem value={9}>9 - Não contribuinte</MenuItem>
                            </Campo>
                        </TabPanel>
                        <Tabs
                            value={aba}
                            onChange={handleAbaChange}
                        >
                            <Tab label="Cliente" />
                            <Tab label="Endereço" />
                        </Tabs>
                    </Content>
                </DialogContent>
                <ButtonsCrud entidade={entidade} form={'formCliente'} handleDeletar={handleDeletar} handleFechar={handleFechar} handleSalvar={handleSalvar} />
            </Dialog>
        )
    }

export default ClienteDialog
